import { motion } from "framer-motion";
import { 
  sectionVariant, 
  staggerContainer, 
  itemVariant,
  viewportOnce,
  viewportOnceNear,
  ease
} from "@/lib/motion";

export interface ProofContent {
  eyebrow: string;
  headline: string;
  description: string;
  contextNote: string;
  quotesLabel: string;
  proofPoints: {
    insight: string;
    before: string;
    after: string;
    signal: string;
  }[];
  clientQuotes: {
    quote: string;
    author: string;
    context: string;
  }[];
}

export const proofDefaults: ProofContent = {
  eyebrow: "Results",
  headline: "Proof through thinking, not claims.",
  description: "What changes after systems are in place, and what clients say once they see it working.",
  contextNote: "We understand the pressure. Budgets under scrutiny. Boards asking harder questions. That's the environment this work lives in.",
  quotesLabel: "What clients say after systems are in place",
  proofPoints: [
    {
      insight: "What usually breaks at scale",
      before: "Acquisition costs rise as you scale. Channels fatigue, audiences shrink, efficiency drops.",
      after: "After systems are in place: cost per acquisition stabilizes because targeting improves, not deteriorates.",
      signal: "Common pattern: 15-40% efficiency gain within first quarter of system implementation.",
    },
    {
      insight: "What changes with retention focus",
      before: "Most brands spend 80% on acquisition, 20% on retention. The inverse of where profit lives.",
      after: "After rebalancing: LTV compounds, payback periods shorten, growth becomes self-funding.",
      signal: "Typical shift: payback period reduction of 2-4 months depending on baseline.",
    },
  ],
  clientQuotes: [
    {
      quote: "They did what three previous agencies couldn't. Connected the systems so we could see what was actually working.",
      author: "CMO",
      context: "Fashion Brand, Series B",
    },
    {
      quote: "Finally, a partner who speaks in outcomes, not impressions.",
      author: "Founder",
      context: "D2C Skincare",
    },
  ],
};

interface ProofProps {
  content?: ProofContent;
}

const Proof = ({ content: c = proofDefaults }: ProofProps) => {
  return (
    <section className="py-12 md:py-16 lg:py-20 border-t border-border relative overflow-hidden">
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 100% 80% at 10% 70%, hsl(216 99% 33% / 0.04) 0%, transparent 50%)`,
        }}
      />
      
      <div className="container-wide relative z-10">
        <motion.div 
          className="max-w-2xl mb-8 md:mb-10 lg:mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={sectionVariant}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-primary" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
              {c.eyebrow}
            </span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight mb-5">
            {c.headline}
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-lg mb-3">
            {c.description}
          </p>
          <p className="text-xs text-muted-foreground/50 max-w-lg">
            {c.contextNote}
          </p>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-2 gap-px bg-border mb-8 md:mb-10 lg:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnceNear}
          variants={staggerContainer}
        >
          {c.proofPoints.map((point) => (
            <motion.div 
              key={point.insight}
              className="bg-background p-6 md:p-8 lg:p-12"
              variants={itemVariant}
            >
              <span className="text-xs uppercase tracking-wider text-primary font-medium">
                {point.insight}
              </span>
              <div className="mt-8 space-y-6">
                <div>
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Before</span>
                  <p className="text-sm text-foreground/60 mt-2 leading-relaxed">{point.before}</p>
                </div>
                <motion.div 
                  className="border-l-2 border-accent/50 pl-6"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.45, ease: ease }}
                >
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">After</span>
                  <p className="text-sm text-foreground mt-2 leading-relaxed">{point.after}</p>
                </motion.div>
                <p className="text-xs text-muted-foreground/60 italic pt-2">{point.signal}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          <motion.span 
            variants={sectionVariant}
            className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-10 block"
          >
            {c.quotesLabel}
          </motion.span>
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
            {c.clientQuotes.map((quote, index) => (
              <motion.div
                key={index}
                className="border-l-2 border-primary/20 pl-8"
                variants={itemVariant}
              >
                <blockquote className="text-lg text-foreground leading-relaxed mb-5">
                  "{quote.quote}"
                </blockquote>
                <div className="text-sm text-muted-foreground">
                  {quote.author}, {quote.context}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Proof;
